"use client";

import { useState } from "react";
import apiClient from "@/libs/api";
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import { useTranslations } from 'next-intl';
import React from "react";
import { trackSeoEvent } from "@/utils/seoAnalytics";

// This component creates Stripe Checkout Sessions for subscriptions and one-time payments.
// It calls the /api/stripe/create-checkout route with the priceId and mode.
const ButtonCheckout = ({
  priceId,
  mode = 'subscription',
  variantId,
  seriesId,
  children
}: {
  priceId?: string;       // Stripe Price ID (preferred)
  mode?: 'subscription' | 'payment';  // Stripe checkout mode
  variantId?: string;     // Legacy LemonSqueezy variant ID (fallback)
  seriesId?: string;
  children?: React.ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const t = useTranslations('Pricing');

  const handlePayment = async () => {
    setIsLoading(true);

    const supabase = createClientComponentClient();
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      window.location.assign('/login');
      return;
    }

    // Free plan or special case
    if (variantId === '0' || priceId === '0') {
      window.location.assign('/submit');
      return;
    }

    try {
      // Use Stripe if priceId is available, otherwise fall back to LemonSqueezy
      if (priceId) {
        const { url }: { url: string } = await apiClient.post(
          "/stripe/create-checkout",
          {
            priceId,
            mode,
            successUrl: window.location.href,
            cancelUrl: window.location.href,
          }
        );
        if (url) {
          let source = 'pricing';
          try {
            const context = JSON.parse(
              window.sessionStorage.getItem('ariaflow:seo-cta-context') || '{}',
            );
            source = context.source || source;
          } catch {
            // Client analytics attribution is optional.
          }
          if (mode === 'subscription') {
            trackSeoEvent('subscription_started', {
              source,
              intent: 'faceless-video',
            });
          }
          window.location.href = url;
        } else {
          console.error("Stripe returned empty checkout URL");
          alert("Failed to create checkout session. Please try again.");
        }
      } else if (variantId) {
        // Fallback to LemonSqueezy for plans without Stripe Price ID
        const { url }: { url: string } = await apiClient.post(
          "/lemonsqueezy/create-checkout",
          {
            variantId,
            mode,
            redirectUrl: window.location.href,
            seriesId,
          }
        );
        if (url) {
          let source = 'pricing';
          try {
            const context = JSON.parse(
              window.sessionStorage.getItem('ariaflow:seo-cta-context') || '{}',
            );
            source = context.source || source;
          } catch {
            // Client analytics attribution is optional.
          }
          if (mode === 'subscription') {
            trackSeoEvent('subscription_started', {
              source,
              intent: 'faceless-video',
            });
          }
          window.location.href = url;
        } else {
          console.error("LemonSqueezy returned empty checkout URL");
          alert("Failed to create checkout session. Please try again.");
        }
      } else {
        console.error("No priceId or variantId provided");
      }
    } catch (e) {
      console.error("结账过程中出错:", e);
      alert("Checkout error. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      className={`btn btn-primary btn-block h-12 relative ${isLoading ? 'bg-opacity-70 hover:bg-opacity-70' : ''
        }`}
      onClick={handlePayment}
      disabled={isLoading}
    >
      {isLoading && (
        <span className="absolute left-4 top-1/2 transform -translate-y-1/2">
          <span className="loading loading-spinner loading-xs"></span>
        </span>
      )}
      <span className={isLoading ? "text-primary-content" : ""}>
        {children || t('Create Series')}
      </span>
    </button>
  );
};

export default ButtonCheckout;
